import { useEffect, useRef, useCallback } from 'react';

interface MemoryManagementOptions {
  maxCacheSize?: number;
  cleanupInterval?: number;
  enableAutoCleanup?: boolean;
}

export const useMemoryManagement = (options: MemoryManagementOptions = {}) => {
  const {
    maxCacheSize = 50,
    cleanupInterval = 300000, // 5 minutes
    enableAutoCleanup = true,
  } = options;

  const cacheRef = useRef<Map<string, any>>(new Map());
  const timersRef = useRef<Set<NodeJS.Timeout>>(new Set());
  const eventListenersRef = useRef<Map<Element, { type: string; listener: EventListener }[]>>(new Map());
  const subscriptionsRef = useRef<Set<() => void>>(new Set());

  // Cache management
  const setCache = useCallback((key: string, value: any) => {
    const cache = cacheRef.current;
    if (cache.size >= maxCacheSize) {
      // Remove oldest entries (simple FIFO)
      const firstKey = cache.keys().next().value;
      if (firstKey) cache.delete(firstKey);
    }
    cache.set(key, value);
  }, [maxCacheSize]);

  const getCache = useCallback((key: string) => {
    return cacheRef.current.get(key);
  }, []);

  const clearCache = useCallback((key?: string) => {
    if (key) {
      cacheRef.current.delete(key);
    } else {
      cacheRef.current.clear();
    }
  }, []);

  // Timer management
  const setManagedTimeout = useCallback((callback: () => void, delay: number) => {
    const timer = setTimeout(() => {
      timersRef.current.delete(timer);
      callback();
    }, delay);
    timersRef.current.add(timer);
    return timer;
  }, []);

  const setManagedInterval = useCallback((callback: () => void, delay: number) => {
    const timer = setInterval(callback, delay);
    timersRef.current.add(timer);
    return timer;
  }, []);

  // Event listener management
  const addManagedEventListener = useCallback((
    element: Element,
    type: string,
    listener: EventListener,
    options?: boolean | AddEventListenerOptions
  ) => {
    element.addEventListener(type, listener, options);
    const listeners = eventListenersRef.current.get(element) || [];
    listeners.push({ type, listener });
    eventListenersRef.current.set(element, listeners);
  }, []);

  // Subscription management
  const addSubscription = useCallback((unsubscribe: () => void) => {
    subscriptionsRef.current.add(unsubscribe);
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    // Clear all timers
    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current.clear();

    // Remove all event listeners
    eventListenersRef.current.forEach((listeners, element) => {
      listeners.forEach(({ type, listener }) => {
        element.removeEventListener(type, listener);
      });
    });
    eventListenersRef.current.clear();

    // Call all unsubscribe functions
    subscriptionsRef.current.forEach(unsubscribe => unsubscribe());
    subscriptionsRef.current.clear();

    // Clear cache
    cacheRef.current.clear();
  }, []);

  // Auto cleanup
  useEffect(() => {
    if (!enableAutoCleanup) return;

    const cleanupTimer = setManagedInterval(() => {
      // Clean up expired cache entries (if we add TTL support later)
      // For now, just ensure we don't exceed cache size
      const cache = cacheRef.current;
      if (cache.size > maxCacheSize) {
        const entriesToRemove = cache.size - maxCacheSize;
        for (let i = 0; i < entriesToRemove; i++) {
          const firstKey = cache.keys().next().value;
          if (firstKey) cache.delete(firstKey);
        }
      }
    }, cleanupInterval);

    return () => {
      clearTimeout(cleanupTimer);
      cleanup();
    };
  }, [enableAutoCleanup, cleanupInterval, maxCacheSize, setManagedInterval, cleanup]);

  return {
    cache: {
      set: setCache,
      get: getCache,
      clear: clearCache,
    },
    timers: {
      setTimeout: setManagedTimeout,
      setInterval: setManagedInterval,
    },
    events: {
      addEventListener: addManagedEventListener,
    },
    subscriptions: {
      add: addSubscription,
    },
    cleanup,
  };
};